<template>
  <div class="block preview">
    <div class="block-header">
      <h4 class="mr-auto">
        Preview
      </h4>

      <a-button
        v-if="!showCode"
        class="mr-2 is-small"
        hotkeys="ctrl+p"
        :loading="loadingTemplatePreview"
        @click="getPreview(true)">
        Preview Template
      </a-button>

      <a-button
        v-if="showCode && type === 'module'"
        class="mr-2 is-small"
        :loading="loadingDebug"
        @click="getDebug(false)">
        Debug
      </a-button>

      <div
        v-if="!showCode"
        class="preview-button-group">
        <a-button
          class="is-small preview-button"
          hotkeys="ctrl+ shift + p"
          :loading="loadingDataPreview"
          @click="getPreview(false)">
          Preview with Data
        </a-button>
        <b-datepicker
          v-model="previewDate"
          position="is-bottom-left"
          :mobile-native="false">
          <template #trigger>
            <b-button
              icon-right="chevron-down"
              class="is-white is-small">
              {{ previewDateFormatted }}
            </b-button>
          </template>
        </b-datepicker>
      </div>

      <button
        class="button output-switcher is-small"
        @click="switchOutputMode">
        {{ showCode ? "View PDF" : "View Code" }}
      </button>
    </div>
    <div class="block-body">
      <div class="output">
        <div
          v-if="!showCode"
          class="preview-output">
          <a
            v-if="!showCode && reportPreviewURL"
            class="button pdf-download-btn is-small"
            :href="reportPreviewURL"
            target="_blank">Download</a>

          <pdf
            v-if="reportPreviewURL"
            :src="reportPreviewURL" />
        </div>

        <div
          v-else
          class="output p-0">
          <v-jsoneditor
            v-model="module"
            :options="{ mode: 'code' }"
            :plus="false"
            height="100%"
            @error="onError" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import VJsoneditor from 'v-jsoneditor'
  import pdf from 'vue-pdf'

  import { formatDate } from '@/utils/Date'
  import moment from 'moment'

  export default {
    name: 'PreviewAndJsonEditor',
    components: {
      pdf,
      VJsoneditor,
    },
    props: {
      value: {
        type: [Object, Array,],
        default: () => {},
      },
      type: {
        type: String,
        required: true,
      },
      fullReport: {
        type: Object,
        default: () => {},
      },
      defaultTimeRange: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        showCode: false,

        modulePreviewURL: null,
        reportPreviewURL: null,
        loadingTemplatePreview: false,
        loadingDataPreview: false,
        loadingDebug: false,
        previewDate: moment().subtract(1, 'days').toDate(),
      }
    },

    computed: {
      previewDateFormatted () {
        return formatDate(this.previewDate, 'll')
      },

      module: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
    },

    errorCaptured (err) {
      if (err?.message?.includes('catch')) return false
      return true
    },
    async mounted () {
      window.addEventListener('keydown', this.shortcutHandler)
    },
    beforeDestroy () {
      window.removeEventListener('keydown', this.shortcutHandler)
    },
    methods: {
      switchOutputMode () {
        this.showCode = !this.showCode
      },
      async getDebug (fakeData = true, errorMessage = 'Error Loading Debug, Fix the errors and try again') {
        this.loadingDebug = true
        try {
          let response = null
          if (this.type === 'report') {
          // Not Supported
          } else if (this.type === 'module') {
            response = await this.$apis.pdfReports.getPdfReportModuleDebug({
              report_module: this.module,
              run_date: formatDate(this.previewDate, 'YYYY-MM-DD'),
              use_fake_data: fakeData,
              default_time_range: this.defaultTimeRange,
            })

            if (response?.errors) {
              this.$buefy.toast.open({
                message: errorMessage,
                type: 'is-warning',
              })
              this.$emit('validation-errors', this.stripErrorPrefix(response.errors))
            } else if (!response) {
              this.$buefy.toast.open({
                message: 'Backend Error, Report this to the devs',
                type: 'is-warning',
              })
            } else {
              this.$emit('validation-errors', [])
            }
          }
        } catch (e) {
          console.log('error loading full debug')
          console.log(e)
        }

        this.loadingDebug = false
      },
      async getPreview (fakeData = true) {
        if (fakeData) {
          this.loadingTemplatePreview = true
        } else {
          this.loadingDataPreview = true
        }

        try {
          if (this.type === 'report') {
            const response = await this.$apis.pdfReports.getPdfReportFullPreview({
              report: {
                ...this.fullReport,
                modules: this.module,
                style: { is_a11y: false, },
              },
              run_date: formatDate(this.previewDate, 'YYYY-MM-DD'),
              use_fake_data: fakeData,
            })
            if (!response) {
              this.$buefy.toast.open({
                message: 'Backend Error, Report this to the devs',
                type: 'is-warning',
              })
            } else {
              this.handlePreviewMetadata(response)
              this.reportPreviewURL = this.getPDFfromBase64(response.pdf)
            }
          } else if (this.type === 'module') {
            const response = await this.$apis.pdfReports.getPdfReportModulePreview({
              report_module: this.module,
              run_date: formatDate(this.previewDate, 'YYYY-MM-DD'),
              use_fake_data: fakeData,
              default_time_range: this.defaultTimeRange,
            })
            if (!response) {
              this.$buefy.toast.open({
                message: 'Error rendering module, report to ingest support.',
                type: 'is-danger',
              })
            } else if (response.errors) {
              this.$buefy.toast.open({
                message: 'Error loading preview, fix the errors and try again',
                type: 'is-warning',
              })
              this.$emit('validation-errors', this.stripErrorPrefix(response.errors))
            } else {
              this.$emit('validation-errors', [])
              if (response.truncated) {
                this.$buefy.toast.open({
                  message: 'Some rows were truncated due to the preview row limit',
                  type: 'is-warning',
                  duration: 5000,
                })
              }
              this.reportPreviewURL = this.getPDFfromBase64(response.pdf)
            }
          }
        } catch (e) {
          if (this.type === 'module') {
            this.$buefy.toast.open({
              message: 'Error rendering module, report to ingest support.',
              type: 'is-danger',
            })
          }
          console.log('error loading full preview')
        }

        if (fakeData) {
          this.loadingTemplatePreview = false
        } else {
          this.loadingDataPreview = false
        }
      },

      getPDFfromBase64 (base64) {
        if (!base64) return null
        const binary = atob(base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i)
        }
        const blob = new Blob([bytes,], { type: 'application/pdf', })
        return URL.createObjectURL(blob)
      },

      handlePreviewMetadata (responseData) {
        if (responseData.truncated) {
          this.$buefy.toast.open({
            message: 'Some rows were truncated due to the preview row limit',
            type: 'is-warning',
            duration: 5000,
          })
        }
        const brokenModules = responseData.broken_modules
        if (brokenModules && brokenModules.length) {
          this.$emit('broken-modules', brokenModules)
          const names = brokenModules.map((m) => m.title || m.id).join(', ')
          this.$buefy.toast.open({
            message: `Some modules failed to render: ${names}`,
            type: 'is-danger',
            duration: 8000,
          })
        }
      },

      stripErrorPrefix (errors) {
        if (!Array.isArray(errors)) return []
        return errors
          .filter(e => e.loc && e.loc[0] === 'report_module')
          .map(e => ({ ...e, loc: e.loc.slice(1), }))
      },

      onError (e) {
        console.log('error', e)
      },
    },
  }
</script>
<style lang="scss">
.block {
  margin-bottom: 6px;
  border: $border-1;
  border-radius: 3px;
  background-color: white;
  height: 100%;
  flex-direction: column;
  display: flex;

  .block-header {
    min-height: 40px;
    align-items: center;
    padding: 0 5px;
    border-bottom: $border-1;
    background-color: white;
    display: flex;

    h4 {
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      padding: 0 10px;
      text-transform: capitalize;
      margin: 0;
      color: black;
    }
  }
  .block-body {
    padding: 0 10px;
    background-color: #fbfbfb;
    height: 100%;
    flex-direction: column;
    display: flex;
  }

  &.library {
    border: $border-1;
    .library-items {
      display: flex;
      flex-direction: column;
    }
    .block-body {
      padding-bottom: 0;
    }
  }

  &.layout {
    max-height: 100%;
    overflow: hidden;

    .block-body {
      height: calc(100% - 40px);
      padding: 0;
    }
  }

  &.preview {
    height: 100%;

    .preview-output {
      position: relative;
      .pdf-download-btn {
        margin: 10px;
      }
    }

    .preview-button-group {
      display: flex;
      align-items: center;
      margin-right: 10px;
      border: $border-1;
      border-color: hsl(0, 0%, 86%);

      .preview-button {
        border-right: 1px solid hsl(0, 0%, 86%);
      }
    }
    .block-header {
      width: 100%;
    }

    .block-body {
      width: 100%;
      height: 100%;
      display: flex;
      overflow: hidden;
    }

    .output {
      background-color: white;
      height: 100%;
      overflow: auto;
    }
  }
}
</style>
